import { useMessageSource } from 'src/i18n/useMessageSource'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Alert, Autocomplete, Box, Grid2, Stack, TextField, Typography } from '@mui/material'
import { FeaturesSortList } from 'src/screens/shared/configuration/feature/FeaturesSortList'
import { LEVEL_TYPE, PROCESS } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { useCallback, useMemo } from 'react'
import {
  getLevelOptions,
  getTranslationForLevel,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'

type FeaturesKapFactsheetFilters = SpecificConfigFilter<'FEATURES_KAP_FACTSHEET'>

export const FeaturesConfigurationKapFactsheetPage = () => {
  const { getMessage } = useMessageSource()

  // filter options
  const factsheetLevelsOptions: Option[] = useMemo(() => getLevelOptions(getMessage), [getMessage])

  const defaultFeaturesKapFactsheetFiltersValues: FeaturesKapFactsheetFilters = useMemo(
    () => ({
      featuresKapFactsheetLevelFilter: factsheetLevelsOptions[0],
    }),
    [factsheetLevelsOptions],
  )

  // sort list filter settings
  const [featuresKapFactsheetFilters, setFeaturesKapFactsheetFilters] = useFilterSettingsFromLocalStorage(
    defaultFeaturesKapFactsheetFiltersValues,
  )

  // filter handlers
  const onFactsheetLevelChange = useCallback(
    (value: Option) => {
      setFeaturesKapFactsheetFilters({
        ...featuresKapFactsheetFilters,
        featuresKapFactsheetLevelFilter: value,
      })
    },
    [featuresKapFactsheetFilters, setFeaturesKapFactsheetFilters],
  )

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.features')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <Stack spacing={2}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage(`label.configuration.criteria.preview.settings`)}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <Autocomplete
                    options={factsheetLevelsOptions}
                    value={featuresKapFactsheetFilters.featuresKapFactsheetLevelFilter}
                    onChange={(_, newVal) => onFactsheetLevelChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForLevel(getMessage, opt.value)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} label={getMessage('label.level')} />}
                  />
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
          <Box>
            <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
          </Box>
          <Stack spacing={5}>
            <FeaturesSortList level={featuresKapFactsheetFilters.featuresKapFactsheetLevelFilter.value as LEVEL_TYPE} />
          </Stack>
        </Stack>
      </PageLayout>
    </ScreenLayout>
  )
}
